import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { ActivityResponse } from './contractsMirror.js';
import type { ActivitySearch } from './searchParams.js';

/**
 * TanStack Query hook for the READ-ONLY coarse Activity feed (CARVE-07). The SERVER owns the
 * audit log + the distinct event types (22-07); the SPA only renders the returned
 * `ActivityResponse`.
 *
 * CRITICAL: this is a plain READ — `GET /api/v1/activity` carries NO tenantId and NEVER
 * mutates (Pitfall 3 / T-22-getmutate). The request goes through `apiFetch` (the only fetch
 * path — `credentials:'include'` + `X-Tenant-Subdomain`, single-flight 401 refresh); the
 * tenant is derived server-side from the auth context.
 *
 * The `?type`/`?from`/`?to`/`?q` filters ARE part of the query key (the server applies them),
 * so a filter change is a distinct, cacheable read of the same read-only surface.
 */

/** Build the `/api/v1/activity` query string from the typed search params. */
export function buildActivityPath(search: ActivitySearch): string {
  const params = new URLSearchParams();
  if (search.type) params.set('type', search.type);
  if (search.actorUserId) params.set('actorUserId', search.actorUserId);
  if (search.from) params.set('from', search.from);
  if (search.to) params.set('to', search.to);
  if (search.q) params.set('q', search.q);
  const qs = params.toString();
  return qs ? `/api/v1/activity?${qs}` : '/api/v1/activity';
}

export function activityQueryKey(search: ActivitySearch): readonly unknown[] {
  return [
    'activity',
    search.type ?? '',
    search.actorUserId ?? '',
    search.from ?? '',
    search.to ?? '',
    search.q ?? '',
  ] as const;
}

export function useActivityQuery(search: ActivitySearch) {
  return useQuery<ActivityResponse>({
    queryKey: activityQueryKey(search),
    // READ-ONLY GET — no tenantId, never mutates.
    queryFn: () => apiFetchJson<ActivityResponse>(buildActivityPath(search)),
  });
}
